/**
 * Notification Hooks
 * React Query hooks for managing notifications
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import notificationsApi, {
  Notification,
  NotificationListResponse,
  NotificationStats,
  NotificationPreference,
  NotificationQueryParams,
  NotificationType,
} from '@/services/notifications-api';

/**
 * Query Keys
 */
export const notificationKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationKeys.all, 'list'] as const,
  list: (params?: NotificationQueryParams) => [...notificationKeys.lists(), params] as const,
  unread: () => [...notificationKeys.all, 'unread'] as const,
  byType: (type: NotificationType) => [...notificationKeys.all, 'type', type] as const,
  overdue: () => [...notificationKeys.all, 'overdue'] as const,
  stats: () => [...notificationKeys.all, 'stats'] as const,
  preferences: () => [...notificationKeys.all, 'preferences'] as const,
};

/**
 * Get all notifications with optional filters
 */
export const useNotifications = (params?: NotificationQueryParams) => {
  return useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: () => notificationsApi.getNotifications(params),
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Refetch every minute
  });
};

/**
 * Get unread notifications only
 */
export const useUnreadNotifications = (limit?: number) => {
  return useQuery({
    queryKey: notificationKeys.unread(),
    queryFn: () => notificationsApi.getUnreadNotifications(limit),
    staleTime: 15000, // 15 seconds - shorter for unread
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};

/**
 * Get overdue notifications
 */
export const useOverdueNotifications = () => {
  return useQuery({
    queryKey: notificationKeys.overdue(),
    queryFn: () => notificationsApi.getOverdueNotifications(),
    staleTime: 30000,
    refetchInterval: 60000,
  });
};

/**
 * Get notifications by type
 */
export const useNotificationsByType = (type: NotificationType, limit?: number) => {
  return useQuery({
    queryKey: notificationKeys.byType(type),
    queryFn: () => notificationsApi.getNotificationsByType(type, limit),
    staleTime: 30000,
    refetchInterval: 60000,
  });
};

/**
 * Get notification statistics
 */
export const useNotificationStats = () => {
  return useQuery({
    queryKey: notificationKeys.stats(),
    queryFn: () => notificationsApi.getNotificationStats(),
    staleTime: 30000,
    refetchInterval: 60000,
  });
};

/**
 * Get notification preferences
 */
export const useNotificationPreferences = () => {
  return useQuery({
    queryKey: notificationKeys.preferences(),
    queryFn: () => notificationsApi.getPreferences(),
    staleTime: 300000, // 5 minutes - preferences change less frequently
  });
};

/**
 * Mark notification as read
 */
export const useMarkAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: number) => notificationsApi.markAsRead(notificationId),
    onMutate: async (notificationId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: notificationKeys.all });

      // Optimistically update all notification lists
      const listQueries = queryClient.getQueriesData<NotificationListResponse>({
        queryKey: notificationKeys.lists(),
      });

      listQueries.forEach(([queryKey, oldData]) => {
        if (oldData) {
          queryClient.setQueryData<NotificationListResponse>(queryKey, {
            ...oldData,
            unread_count: Math.max(0, oldData.unread_count - 1),
            results: oldData.results.map((notif) =>
              notif.id === notificationId ? { ...notif, is_read: true } : notif
            ),
          });
        }
      });

      return { listQueries };
    },
    onError: (err, notificationId, context) => {
      // Rollback optimistic updates
      if (context?.listQueries) {
        context.listQueries.forEach(([queryKey, oldData]) => {
          queryClient.setQueryData(queryKey, oldData);
        });
      }
      toast.error('Failed to mark notification as read');
    },
    onSuccess: () => {
      // Invalidate to ensure consistency
      queryClient.invalidateQueries({ queryKey: notificationKeys.stats() });
    },
  });
};

/**
 * Mark all notifications as read
 */
export const useMarkAllAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificationsApi.markAllAsRead(),
    onSuccess: (data) => {
      // Invalidate all notification queries
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      toast.success(`Marked ${data.updated_count} notifications as read`);
    },
    onError: () => {
      toast.error('Failed to mark all notifications as read');
    },
  });
};

/**
 * Delete notification
 */
export const useDeleteNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: number) => notificationsApi.deleteNotification(notificationId),
    onMutate: async (notificationId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: notificationKeys.all });

      // Optimistically remove notification from all lists
      const listQueries = queryClient.getQueriesData<NotificationListResponse>({
        queryKey: notificationKeys.lists(),
      });

      listQueries.forEach(([queryKey, oldData]) => {
        if (oldData) {
          const deletedNotification = oldData.results.find((n) => n.id === notificationId);
          queryClient.setQueryData<NotificationListResponse>(queryKey, {
            ...oldData,
            count: oldData.count - 1,
            unread_count: deletedNotification?.is_read
              ? oldData.unread_count
              : Math.max(0, oldData.unread_count - 1),
            results: oldData.results.filter((notif) => notif.id !== notificationId),
          });
        }
      });

      return { listQueries };
    },
    onError: (err, notificationId, context) => {
      // Rollback optimistic updates
      if (context?.listQueries) {
        context.listQueries.forEach(([queryKey, oldData]) => {
          queryClient.setQueryData(queryKey, oldData);
        });
      }
      toast.error('Failed to delete notification');
    },
    onSuccess: () => {
      toast.success('Notification deleted');
      queryClient.invalidateQueries({ queryKey: notificationKeys.stats() });
    },
  });
};

/**
 * Update notification preferences
 */
export const useUpdatePreferences = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (preferences: Partial<NotificationPreference>) =>
      notificationsApi.updatePreferences(preferences),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.preferences() });
      toast.success('Notification preferences updated');
    },
    onError: () => {
      toast.error('Failed to update notification preferences');
    },
  });
};
